// OCT-7341: TreatyApp: File Activity panel — add search, filters, and a count of matching activities - Frontend : ashish.atwal@resolutionlife.com
// Filter row for the Activity panel: User, Event, Folder and File dropdowns plus
// a Clear button. The User/Folder/File options are derived from the currently
// loaded events and passed in by the panel. All filtering is applied client-side,
// so these controls only report the selected values upward via onChange.
import { ChangeEvent, FC } from 'react';
import { Button, Flex, Select } from '@chakra-ui/react';

import { EventType } from '../../../graphql/generated';
import {
  ActivityFilterState,
  ActivityUserOption,
  isActivityFilterActive,
} from '../activity-filtering';

const eventTypeLabelMap: Record<EventType, string> = {
  [EventType.FILE_ARCHIVED]: 'Archived',
  [EventType.FILE_CREATED]: 'Uploads',
  [EventType.FILE_DOWNLOADED]: 'Downloads',
  [EventType.FILE_DELETED]: 'Deletes',
  [EventType.FILE_UPDATED]: 'Revisions',
  [EventType.FOLDER_CREATED]: 'New Folder',
  [EventType.FOLDER_DOWNLOADED]: 'Folder Downloads',
  [EventType.PII_APPROVED]: 'PII Approvals',
  [EventType.FILE_MOVED]: 'Moves',
  [EventType.FILE_RENAMED]: 'Renames',
};

type Props = {
  filter: ActivityFilterState;
  users: ActivityUserOption[];
  folders: string[];
  files: string[];
  onChange: (filter: ActivityFilterState) => void;
  onClear: () => void;
};

export const FileActivityListFilters: FC<Props> = ({
  filter,
  users,
  folders,
  files,
  onChange,
  onClear,
}) => {
  const handleUserChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filter, user: e.target.value });
  };

  const handleEventChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onChange({
      ...filter,
      eventType: value ? (value as EventType) : undefined,
    });
  };

  const handleFolderChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filter, folder: e.target.value });
  };

  const handleFileChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filter, file: e.target.value });
  };

  return (
    <Flex gridGap={2} align="center" wrap="wrap">
      <Select
        size="sm"
        placeholder="All users"
        value={filter.user}
        onChange={handleUserChange}
        data-testid="file-activity-user-filter"
        maxW="40"
      >
        {users.map((user) => (
          <option key={user.id} value={user.id}>
            {user.name}
          </option>
        ))}
      </Select>

      <Select
        size="sm"
        placeholder="All events"
        value={filter.eventType ?? ''}
        onChange={handleEventChange}
        data-testid="file-activity-type-filter"
        maxW="40"
      >
        {Object.entries(eventTypeLabelMap).map(([key, value]) => (
          <option key={key} value={key}>
            {value}
          </option>
        ))}
      </Select>

      <Select
        size="sm"
        placeholder="All folders"
        value={filter.folder}
        onChange={handleFolderChange}
        data-testid="file-activity-folder-filter"
        maxW="40"
      >
        {folders.map((folder) => (
          <option key={folder} value={folder}>
            {folder}
          </option>
        ))}
      </Select>

      <Select
        size="sm"
        placeholder="All files"
        value={filter.file}
        onChange={handleFileChange}
        data-testid="file-activity-file-filter"
        maxW="40"
      >
        {files.map((file) => (
          <option key={file} value={file}>
            {file}
          </option>
        ))}
      </Select>

      <Button
        size="sm"
        variant="outline"
        onClick={onClear}
        isDisabled={!isActivityFilterActive(filter)}
        data-testid="file-activity-clear-filters"
      >
        Clear
      </Button>
    </Flex>
  );
};
